/**
 * OnboardingWizard — Interactive 3-step setup wizard for new gym owners.
 *
 * Shows as a full-screen overlay after first login.
 * Dismissed permanently once all steps are "visited" OR via the skip button.
 * Key: 'fitxero_onboarding_done_v1'
 */
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  UserPlus,
  Package,
  CheckCircle2,
  ArrowRight,
  X,
  Dumbbell,
  Sparkles,
  Check,
} from 'lucide-react';

const STORAGE_KEY = 'fitxero_onboarding_done_v1';

const STEPS = [
  {
    id: 'member',
    icon: UserPlus,
    color: '#3b82f6',
    bg: 'rgba(59,130,246,0.15)',
    border: 'rgba(59,130,246,0.3)',
    title: 'Add Your First Member',
    description:
      'Start building your member base. Add a member with their name, phone, and membership plan. This takes under 60 seconds.',
    cta: 'Add a Member',
    path: '/admin/members/new',
    tip: '💡 Tip: You can import members in bulk from the Members page later.',
  },
  {
    id: 'plan',
    icon: Package,
    color: '#a855f7',
    bg: 'rgba(168,85,247,0.15)',
    border: 'rgba(168,85,247,0.3)',
    title: 'Create a Membership Plan',
    description:
      'Set up your gym\'s pricing plans (e.g., "Monthly ₹1,500" or "Quarterly ₹4,000"). Members will be assigned to these plans.',
    cta: 'Create a Plan',
    path: '/admin/plans',
    tip: '💡 Tip: You can create multiple plans for different durations and pricing.',
  },
  {
    id: 'attendance',
    icon: CheckCircle2,
    color: '#22c55e',
    bg: 'rgba(34,197,94,0.15)',
    border: 'rgba(34,197,94,0.3)',
    title: 'Mark Your First Check-In',
    description:
      'Use the Attendance page to mark when members arrive at your gym. You can also generate a QR code for self-check-in.',
    cta: 'Go to Attendance',
    path: '/admin/attendance',
    tip: '💡 Tip: Share the QR code check-in link with members so they can check in themselves.',
  },
];

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [visited, setVisited] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('fitxero_onboarding_visited') || '[]');
    } catch {
      return [];
    }
  });
  const [closing, setClosing] = useState(false);

  // Show on first visit (only if not dismissed before)
  useEffect(() => {
    const done = localStorage.getItem(STORAGE_KEY) === '1';
    if (!done) {
      // Small delay so the dashboard loads first
      const t = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  // Lock body scroll while wizard is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const dismiss = () => {
    setClosing(true);
    setTimeout(() => {
      setOpen(false);
      setClosing(false);
      localStorage.setItem(STORAGE_KEY, '1');
    }, 300);
  };

  const markVisited = idx => {
    const next = [...new Set([...visited, STEPS[idx].id])];
    setVisited(next);
    localStorage.setItem('fitxero_onboarding_visited', JSON.stringify(next));
  };

  const handleAction = () => {
    markVisited(step);
    dismiss();
    navigate(STEPS[step].path);
  };

  const handleNext = () => {
    markVisited(step);
    if (step < STEPS.length - 1) {
      setStep(s => s + 1);
    } else {
      dismiss();
    }
  };

  const isVisited = id => visited.includes(id);
  const allDone = STEPS.every(s => isVisited(s.id));

  if (!open) {
    return null;
  }

  const current = STEPS[step];
  const StepIcon = current.icon;
  const completedCount = STEPS.filter(s => isVisited(s.id)).length;

  const content = (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        opacity: closing ? 0 : 1,
        transition: 'opacity 0.3s ease',
      }}
    >
      {/* Backdrop */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.75)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
        onClick={dismiss}
      />

      {/* Modal */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 640,
          background: 'var(--card, #fff)',
          border: '1px solid var(--border, rgba(0,0,0,0.08))',
          borderRadius: 24,
          boxShadow: '0 40px 100px rgba(0,0,0,0.5)',
          overflow: 'hidden',
          animation: closing ? 'none' : 'wizardIn 0.35s cubic-bezier(0.16,1,0.3,1)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Top gradient bar */}
        <div
          style={{
            height: 4,
            background: `linear-gradient(90deg, ${current.color}, ${current.color}88)`,
            transition: 'background 0.4s',
          }}
        />

        {/* Progress bar (thin strip behind top bar) */}
        <div style={{ height: 3, background: 'rgba(128,128,128,0.08)', margin: '0 24px' }}>
          <div
            style={{
              height: '100%',
              borderRadius: 99,
              width: `${((step + 1) / STEPS.length) * 100}%`,
              background: current.color,
              transition: 'width 0.5s cubic-bezier(0.16,1,0.3,1), background 0.4s',
            }}
          />
        </div>

        {/* Header */}
        <div
          style={{
            padding: '24px 28px 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {/* Logo + brand */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: 'linear-gradient(135deg, #10b981, #0d9488)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Dumbbell size={18} color="#fff" />
            </div>
            <div>
              <p
                style={{
                  margin: 0,
                  fontSize: 14,
                  fontWeight: 800,
                  color: 'var(--text-primary, #111)',
                  letterSpacing: '-0.3px',
                }}
              >
                Fitxero Setup
              </p>
              <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted, #888)' }}>
                Step {step + 1} of {STEPS.length}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={dismiss}
            title="Skip setup"
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: 'rgba(128,128,128,0.1)',
              border: '1px solid rgba(128,128,128,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'var(--text-muted, #888)',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'rgba(239,68,68,0.1)';
              e.currentTarget.style.color = '#ef4444';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'rgba(128,128,128,0.1)';
              e.currentTarget.style.color = 'var(--text-muted, #888)';
            }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Step indicators */}
        <div style={{ padding: '20px 28px 0', display: 'flex', gap: 8 }}>
          {STEPS.map((s, i) => {
            const done = isVisited(s.id);
            const active = i === step;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setStep(i)}
                style={{
                  flex: 1,
                  height: 5,
                  borderRadius: 99,
                  border: 'none',
                  cursor: 'pointer',
                  background: done ? s.color : active ? current.color : 'rgba(128,128,128,0.15)',
                  transition: 'background 0.3s',
                  padding: 0,
                }}
                title={s.title}
              />
            );
          })}
        </div>

        {/* Main content */}
        <div style={{ padding: '28px 28px 0' }}>
          {/* Step icon + title */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 20 }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 16,
                background: current.bg,
                border: `1.5px solid ${current.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                transition: 'all 0.4s',
              }}
            >
              <StepIcon size={26} style={{ color: current.color }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p
                style={{
                  margin: '0 0 6px',
                  fontSize: 22,
                  fontWeight: 900,
                  color: 'var(--text-primary, #111)',
                  letterSpacing: '-0.5px',
                  lineHeight: 1.2,
                }}
              >
                {current.title}
              </p>
              <p
                style={{
                  margin: 0,
                  fontSize: 14,
                  lineHeight: 1.65,
                  color: 'var(--text-secondary, #555)',
                }}
              >
                {current.description}
              </p>
            </div>
          </div>

          {/* Tip box */}
          <div
            style={{
              background: current.bg,
              border: `1px solid ${current.border}`,
              borderRadius: 12,
              padding: '12px 16px',
              marginBottom: 24,
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: 13,
                color: 'var(--text-secondary, #555)',
                lineHeight: 1.5,
              }}
            >
              {current.tip}
            </p>
          </div>
        </div>

        {/* Step checklist — show all steps as a mini overview */}
        <div style={{ padding: '0 28px 24px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {STEPS.map((s, i) => {
            const done = isVisited(s.id);
            const active = i === step;
            const S = s.icon;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setStep(i)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 14px',
                  borderRadius: 12,
                  border: active
                    ? `1.5px solid ${s.color}`
                    : done
                      ? `1px solid rgba(34,197,94,0.25)`
                      : '1px solid rgba(128,128,128,0.12)',
                  background: active
                    ? s.bg
                    : done
                      ? 'rgba(34,197,94,0.06)'
                      : 'rgba(128,128,128,0.04)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s',
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 9,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    background: done
                      ? 'rgba(34,197,94,0.15)'
                      : active
                        ? s.bg
                        : 'rgba(128,128,128,0.08)',
                    border: done
                      ? '1px solid rgba(34,197,94,0.3)'
                      : active
                        ? `1px solid ${s.border}`
                        : '1px solid rgba(128,128,128,0.15)',
                  }}
                >
                  {done ? (
                    <Check size={14} style={{ color: '#22c55e' }} />
                  ) : (
                    <S size={14} style={{ color: active ? s.color : 'rgba(128,128,128,0.5)' }} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 13,
                      fontWeight: 700,
                      color: done ? '#22c55e' : active ? s.color : 'var(--text-secondary, #666)',
                      textDecoration: done ? 'line-through' : 'none',
                    }}
                  >
                    {s.title}
                  </p>
                </div>
                {active && (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: s.color,
                      background: s.bg,
                      border: `1px solid ${s.border}`,
                      borderRadius: 99,
                      padding: '2px 8px',
                      flexShrink: 0,
                    }}
                  >
                    Current
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Footer actions */}
        <div
          style={{
            padding: '16px 28px 28px',
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            borderTop: '1px solid rgba(128,128,128,0.08)',
          }}
        >
          {/* Skip / Back */}
          <button
            type="button"
            onClick={dismiss}
            style={{
              padding: '10px 18px',
              borderRadius: 12,
              border: '1.5px solid rgba(128,128,128,0.15)',
              background: 'transparent',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--text-muted, #888)',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'rgba(128,128,128,0.3)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'rgba(128,128,128,0.15)';
            }}
          >
            {allDone ? 'Close' : 'Skip Setup'}
          </button>

          {/* Next step (without navigating) */}
          {step < STEPS.length - 1 && (
            <button
              type="button"
              onClick={handleNext}
              style={{
                padding: '10px 18px',
                borderRadius: 12,
                border: `1.5px solid ${current.border}`,
                background: current.bg,
                fontSize: 13,
                fontWeight: 600,
                color: current.color,
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              Next Step
            </button>
          )}

          {/* Primary CTA — navigate to action */}
          <button
            type="button"
            onClick={handleAction}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              padding: '11px 20px',
              borderRadius: 12,
              border: 'none',
              background: `linear-gradient(135deg, ${current.color}, ${current.color}cc)`,
              fontSize: 14,
              fontWeight: 700,
              color: '#fff',
              cursor: 'pointer',
              boxShadow: `0 6px 20px ${current.color}40`,
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-1.5px)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'none';
            }}
          >
            <Sparkles size={15} />
            {current.cta}
            <ArrowRight size={15} />
          </button>
        </div>

        {/* Completion badge */}
        {completedCount > 0 && (
          <div
            style={{
              position: 'absolute',
              top: 60,
              right: 28,
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              background: 'rgba(34,197,94,0.1)',
              border: '1px solid rgba(34,197,94,0.25)',
              borderRadius: 99,
              padding: '3px 10px',
            }}
          >
            <Check size={11} style={{ color: '#22c55e' }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: '#22c55e' }}>
              {completedCount}/{STEPS.length} done
            </span>
          </div>
        )}
      </div>

      <style>{`
        @keyframes wizardIn {
          from { opacity: 0; transform: scale(0.92) translateY(24px); }
          to   { opacity: 1; transform: scale(1)   translateY(0); }
        }
      `}</style>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
}
